import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatUSD } from "@/lib/api";
import { FolderOpen, UsersThree, CurrencyDollar, Clock } from "@phosphor-icons/react";
import { AdminMetrics } from "@/components/AdminMetrics";
import { AdminGlobalMessages } from "@/components/AdminGlobalMessages";

const STATUS_LABEL = {
  submitted: "Submitted",
  preproduction: "Preproduction",
  design: "Design",
  review: "Review",
  final: "Final",
  delivered: "Delivered",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([api.get("/admin/stats"), api.get("/projects")]);
        setStats(s.data);
        setProjects(p.data.slice(0, 8));
      } catch (_) {}
    })();
  }, []);

  const tiles = [
    { label: "Total projects", value: stats?.total_projects ?? "—", icon: FolderOpen },
    { label: "Open", value: stats?.open_projects ?? "—", icon: Clock },
    { label: "Clients", value: stats?.total_clients ?? "—", icon: UsersThree },
    { label: "Revenue", value: stats ? formatUSD(stats.revenue) : "—", icon: CurrencyDollar },
  ];

  return (
    <AdminLayout>
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Dashboard</div>
          <h1 className="font-display text-5xl md:text-6xl mt-2">Control room.</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/admin/invoices" data-testid="dashboard-new-invoice"
            className="border border-[#FF2D2D] text-[#FF2D2D] hover:bg-[#FF2D2D] hover:text-white transition-colors px-4 py-2 text-xs uppercase tracking-wider font-bold">
            + Invoice
          </Link>
          <Link to="/admin/receipts" data-testid="dashboard-new-receipt"
            className="border border-[#28C878] text-[#28C878] hover:bg-[#28C878] hover:text-black transition-colors px-4 py-2 text-xs uppercase tracking-wider font-bold">
            + Receipt
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#262626] border border-[#262626] mb-10" data-testid="admin-stats">
        {tiles.map((t, i) => {
          const Icon = t.icon;
          return (
            <div key={i} className="bg-[#0a0a0a] p-5">
              <Icon size={20} className="text-[#FF2D2D]" />
              <div className="font-display text-3xl mt-3">{t.value}</div>
              <div className="text-xs text-[#a3a3a3] uppercase tracking-wider">{t.label}</div>
            </div>
          );
        })}
      </div>

      {stats?.by_status && (
        <div className="mb-10">
          <h2 className="font-display text-2xl mb-4 uppercase">By status</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {["submitted", "preproduction", "design", "review", "final", "delivered"].map((s) => (
              <div key={s} className="border border-[#262626] bg-[#0a0a0a] p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">{STATUS_LABEL[s]}</div>
                <div className="font-display text-3xl mt-1 text-[#FF2D2D]">{stats.by_status[s] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workload */}
      {stats && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl uppercase">Designer workload</h2>
            <Link to="/admin/designers" className="text-sm text-[#FF2D2D] hover:underline" data-testid="manage-designers">Manage team →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="workload-grid">
            <div className={`border p-4 ${stats.unassigned > 0 ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626] bg-[#0a0a0a]"}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Unassigned</div>
              <div className="font-display text-3xl mt-1 text-[#FF2D2D] tabular-nums">{stats.unassigned}</div>
              <div className="text-xs text-[#a3a3a3] mt-1">Need a designer</div>
            </div>
            {(stats.workload || []).map((w) => {
              const heavy = w.active_projects >= 5;
              return (
                <div key={w.designer_id} className={`border p-4 ${heavy ? "border-[#FF2D2D]" : "border-[#262626]"} bg-[#0a0a0a]`} data-testid={`workload-${w.designer_id}`}>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">{w.name}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-display text-3xl tabular-nums">{w.active_projects}</span>
                    <span className="text-xs text-[#a3a3a3]">active</span>
                  </div>
                  {heavy && <div className="text-xs text-[#FF2D2D] mt-1">⚠ At capacity</div>}
                </div>
              );
            })}
            {(!stats.workload || stats.workload.length === 0) && (
              <div className="border border-dashed border-[#262626] p-4 text-xs text-[#a3a3a3] flex items-center">
                No designers assigned to projects yet.
              </div>
            )}
          </div>
        </div>
      )}

      <AdminMetrics />

      <AdminGlobalMessages />

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl uppercase">Recent projects</h2>
        <Link to="/admin/projects" className="text-sm text-[#FF2D2D] hover:underline" data-testid="see-all-projects">See all →</Link>
      </div>
      <div className="border border-[#262626] divide-y divide-[#262626]">
        {projects.map((p) => (
          <Link
            key={p.id}
            to={`/admin/projects/${p.id}`}
            data-testid={`admin-recent-${p.id}`}
            className="grid grid-cols-12 gap-4 p-4 bg-[#0a0a0a] hover:bg-[#121212] transition-colors items-center"
          >
            <div className="col-span-12 md:col-span-5">
              <div className="font-semibold">{p.title}</div>
              <div className="text-xs text-[#a3a3a3]">{p.client_name} · {p.service_type}</div>
            </div>
            <div className="col-span-6 md:col-span-3 text-sm text-[#FF2D2D]">{STATUS_LABEL[p.status] || p.status}</div>
            <div className="col-span-6 md:col-span-2 text-sm">
              {p.payment_method === "points" ? `${p.total_points} pts` : formatUSD(p.total_usd)}
            </div>
            <div className="col-span-12 md:col-span-2 text-xs text-[#a3a3a3] capitalize">{p.payment_status?.replace("_", " ")}</div>
          </Link>
        ))}
        {projects.length === 0 && (
          <div className="bg-[#0a0a0a] p-8 text-center text-[#a3a3a3]">No projects yet.</div>
        )}
      </div>
    </AdminLayout>
  );
}
